import { NoInfer } from 'react-redux';

import { ActionReducerMapBuilder, Draft, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { RootState } from 'store/rootReducer';
import {
  FulfilledActionType,
  PendingActionType,
  RejectedActionType,
  errorOr,
  fulfilled,
  pending,
  rejected
} from 'store/storeHelpers';

import { BebyggelseobjektPubliceradVersionIndex, ModelError, State } from 'models';
import { HTTP_STATUS } from 'models/appModels/Request';

import BebyggelseApi from 'api/bebyggelseApi';

const requestCondition = (getState: () => unknown): false | undefined => {
  const { bebyggelseobjektVersioner } = getState() as RootState;
  if (bebyggelseobjektVersioner.requestStatus === 'pending') return false;
};

export const getBebyggelseobjektVersioner = createAsyncThunk<
  Array<BebyggelseobjektPubliceradVersionIndex>,
  string,
  { rejectValue: ModelError }
>(
  'get/bebyggelseobjektversioner',
  async (id: string, { rejectWithValue }): Promise<any> => {
    const response: Response = await BebyggelseApi.bebyggelseobjekt.getVersioner(id);
    return errorOr(response, rejectWithValue, [HTTP_STATUS.SERVICE_UNAVAILABLE, HTTP_STATUS.NOT_FOUND]);
  },
  {
    condition: (id: string, { getState }) => requestCondition(getState)
  }
);

const initialState: State<Array<BebyggelseobjektPubliceradVersionIndex>> = {
  requestStatus: 'idle',
  requestType: undefined,
  data: undefined,
  currentRequestId: undefined,
  error: undefined
};

const bebyggelseobjektVersionerSlice = createSlice({
  name: 'bebyggelseobjektVersioner',
  initialState,
  reducers: {
    clearBebyggelseobjektVersioner: (state: Draft<State<Array<BebyggelseobjektPubliceradVersionIndex>>>) => {
      state.data = undefined;
    }
  },
  extraReducers: (
    builder: ActionReducerMapBuilder<NoInfer<State<Array<BebyggelseobjektPubliceradVersionIndex>>>>
  ): void => {
    builder.addCase(
      getBebyggelseobjektVersioner.pending,
      (state: Draft<State<Array<BebyggelseobjektPubliceradVersionIndex>>>, action: PendingActionType): void =>
        pending(state, action)
    );
    builder.addCase(
      getBebyggelseobjektVersioner.fulfilled,
      (
        state: Draft<State<Array<BebyggelseobjektPubliceradVersionIndex>>>,
        action: FulfilledActionType<Array<BebyggelseobjektPubliceradVersionIndex>>
      ): void => fulfilled(state, action)
    );
    builder.addCase(
      getBebyggelseobjektVersioner.rejected,
      (state: Draft<State<Array<BebyggelseobjektPubliceradVersionIndex>>>, action: RejectedActionType): void =>
        rejected(state, action)
    );
  }
});

export const { clearBebyggelseobjektVersioner } = bebyggelseobjektVersionerSlice.actions;
export default bebyggelseobjektVersionerSlice.reducer;
